import React from "react";
import OverviewPage from "./pages/overview/OverviewPage";
import AdvertiserPage from "./pages/advertiser/AdvertiserPage";
import PPCPage from "./pages/ppc/PPCPage";
import CompetitorsPage from "./pages/competitors/CompetitorsPage";
import ComingSoonPage from "./pages/ComingSoonPage";
import { channelGroups } from "../../core/data/channelOrder";
import { CHANNEL_NAMES } from "../../core/data/marketOverviewText";

const comingSoon = title => function Placeholder() { return <ComingSoonPage title={title} />; };

// Every report page. Pages without a channel come first, in this order;
// channel pages follow in the same order as the channel tiles
// (core/data/channelOrder.js): channels seen, in Google's page order, then
// channels nobody appeared in, then channels not checked yet. A tab for a
// channel nobody appeared in, or that isn't checked yet, shows but can't be
// clicked.
// Each page receives the report context for the current filters:
//   { overview, advertiser, onSetType, results, competitors, pinKey,
//     highlightKeys, onPin, onShowTab, onOpenAdvertiser, onOpenLinkPanel }
// fullHeight pages fit the window and scroll inside their own panels.
// showsPin: the tab bar shows the pinned advertiser (also on every channel page).
export const REPORT_PAGES = [
  { id: "overview", label: "Market Snapshot", component: OverviewPage },
  { id: "advertiser", label: "Advertiser Snapshot", component: AdvertiserPage, showsPin: true },
  { id: "ppc", label: CHANNEL_NAMES.ppc, channel: "ppc", component: PPCPage },
  { id: "shopping", label: CHANNEL_NAMES.shopping, channel: "shopping", component: comingSoon(`The ${CHANNEL_NAMES.shopping} page`) },
  { id: "local", label: CHANNEL_NAMES.local, channel: "local", component: comingSoon(`The ${CHANNEL_NAMES.local} page`) },
  { id: "seo", label: CHANNEL_NAMES.seo, channel: "seo", component: comingSoon(`The ${CHANNEL_NAMES.seo} page`) },
  { id: "lsa", label: CHANNEL_NAMES.lsa, channel: "lsa", component: comingSoon(`The ${CHANNEL_NAMES.lsa} page`) },
  // Not searched yet: always shown as tabs that can't be clicked.
  { id: "ai", label: CHANNEL_NAMES.ai, channel: "ai", notChecked: true },
  { id: "bing", label: CHANNEL_NAMES.bing, channel: "bing", notChecked: true },
  // Replaced by Market Overview; kept in case it's wanted again.
  { id: "competitors", label: "Competitors", component: CompetitorsPage, alwaysHidden: true },
];

// Does this page show the pinned advertiser chip? Never on Market Overview.
export function pageShowsPin(page) {
  return Boolean(page && (page.showsPin || page.channel));
}

// { pages, tabs, hiddenChannels }
//   pages: the pages that can be opened
//   tabs:  every tab in order, each { ...page, disabled, reason } - reason
//          "none seen" or "not checked" for a tab that can't be clicked
//   hiddenChannels: channel pages nobody appeared in
export function visiblePages(context) {
  const { seen, noneSeen, notChecked } = channelGroups(context.overview.serpMap);
  const fixed = REPORT_PAGES.filter(page => !page.channel && !page.alwaysHidden);
  const byChannel = channel => REPORT_PAGES.find(page => page.channel === channel);
  const seenPages = seen.map(byChannel).filter(Boolean);
  const emptyPages = noneSeen.map(byChannel).filter(Boolean);
  const uncheckedPages = notChecked.map(byChannel).filter(Boolean);
  return {
    pages: [...fixed, ...seenPages],
    tabs: [
      ...[...fixed, ...seenPages].map(page => ({ ...page, disabled: false, reason: null })),
      ...emptyPages.map(page => ({ ...page, disabled: true, reason: "none seen" })),
      ...uncheckedPages.map(page => ({ ...page, disabled: true, reason: "not checked" })),
    ],
    hiddenChannels: emptyPages,
  };
}
